import { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Icon, IconButton, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { TbChevronDown, TbChevronRight } from 'react-icons/tb';
import type { RankingItem } from '../../../types/reporting';
import type { ReportingComparison } from '../../../types/reporting';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import { NEGATIVE_METRICS } from '../../../types/reporting';
import type { MetricOption, MetricFormat } from '../components/RankingBarChart';
import SortArrowIcon from '../../../components/SortArrowIcon';
import { getSourceColor, getSourceLabel } from '../../../components/SourceBadge';

interface RankingTableWidgetProps {
    data: RankingItem[] | undefined;
    isLoading: boolean;
    metricOptions: MetricOption[];
    defaultMetric?: string;
    controlledMetric?: string;
    comparison?: ReportingComparison;
    preset?: string;
    maxItems?: number;
    entityLabel: string;
    labelFormatter?: (value: string) => string;
    onItemClick?: (item: RankingItem) => void;
    initialSortOrder?: 'asc' | 'desc';
    /** Allow expanding each row into per-marketplace sub-rows (uses item.bySource) */
    marketplaceBreakdown?: boolean;
}

const formatCompactCurrency = (value: number): string => {
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    if (absValue >= 1000000) return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `${sign}$${(absValue / 1000).toFixed(1)}K`;
    return `${sign}$${Math.round(absValue).toLocaleString()}`;
};

const formatCompactPercent = (value: number) => `${(value || 0).toFixed(1)}%`;
const formatCompactNumber = (value: number) => Math.round(value || 0).toLocaleString();

const getFormatter = (format: MetricFormat) => {
    switch (format) {
        case 'currency':
            return formatCompactCurrency;
        case 'percent':
            return formatCompactPercent;
        default:
            return formatCompactNumber;
    }
};

const getComparisonDisplay = (
    item: RankingItem,
    metric: string,
    format: MetricFormat
) => {
    if (item.change && item.change[metric] !== undefined) {
        const change = item.change[metric];
        const suffix = format === 'percent' ? 'pp' : '%';
        return {
            text: `${change >= 0 ? '+' : '-'}${Math.abs(change).toFixed(1)}${suffix}`,
            isPositive: change >= 0,
        };
    }

    return null;
};

const RankingTableWidget = ({
    data,
    isLoading,
    metricOptions,
    defaultMetric,
    controlledMetric,
    comparison,
    preset,
    maxItems,
    entityLabel,
    labelFormatter,
    onItemClick,
    initialSortOrder = 'desc',
    marketplaceBreakdown = false,
}: RankingTableWidgetProps) => {
    const metric = controlledMetric || defaultMetric || metricOptions[0]?.value || '';
    const metricOption = metricOptions.find((option) => option.value === metric) || metricOptions[0];
    const formatter = getFormatter(metricOption?.format || 'number');
    const comparisonLabel = getSummaryComparisonLabel(preset) ?? 'vs Prior';
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    useEffect(() => {
        setSortOrder(initialSortOrder);
    }, [initialSortOrder]);

    const showBreakdown = marketplaceBreakdown && (data ?? []).some((d) => d?.bySource && Object.keys(d.bySource).length > 0);

    const toggleRow = (key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Sources for a row, ordered by the current metric value (descending).
    const getRowSources = (item: RankingItem): string[] => {
        const bs = item.bySource;
        if (!bs) return [];
        return Object.keys(bs).sort((a, b) => {
            const av = Number((bs[a] as any)?.[metric]) || 0;
            const bv = Number((bs[b] as any)?.[metric]) || 0;
            return bv - av;
        });
    };

    const rows = useMemo(
        () =>
            [...(data ?? [])]
                .filter((item) => item && item.name)
                .sort((a, b) => {
                    const aVal = ((a as any)[metric] || 0);
                    const bVal = ((b as any)[metric] || 0);
                    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
                })
                .slice(0, maxItems)
                .map((item) => ({
                    ...item,
                    displayName: labelFormatter ? labelFormatter(item.name) : item.name,
                })),
        [data, labelFormatter, maxItems, metric, sortOrder]
    );

    if (isLoading && (!data || data.length === 0)) {
        return <Skeleton height="100%" borderRadius="lg" />;
    }

    if (!data || data.length === 0 || !metricOption) {
        return (
            <Box h="100%" display="flex" alignItems="center" justifyContent="center">
                <Text color="gray.500">No data available</Text>
            </Box>
        );
    }

    return (
        <Box
            h="100%"
            overflowY="auto"
            overflowX="hidden"
            sx={{
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { bg: 'transparent' },
                '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: 'full', '&:hover': { bg: 'gray.300' } },
            }}
        >
            <Table size="sm" variant="unstyled" sx={{ tableLayout: 'fixed' }}>
                <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
                    <Tr borderBottom="1px solid" borderColor="gray.200">
                        <Th
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="semibold"
                            textTransform="capitalize"
                            letterSpacing="normal"
                            px={4}
                            py={2.5}
                            w={comparison ? '46%' : '56%'}
                        >
                            {entityLabel}
                        </Th>
                        <Th
                            fontSize="xs"
                            color="gray.500"
                            fontWeight="semibold"
                            textTransform="capitalize"
                            letterSpacing="normal"
                            textAlign="right"
                            px={4}
                            py={2.5}
                            w={comparison ? '28%' : '44%'}
                        >
                            <Flex justify="flex-end" align="center" gap={1}>
                                <Text>{metricOption.label}</Text>
                                <IconButton
                                    aria-label="Toggle sort order"
                                    icon={<SortArrowIcon direction={sortOrder} size={14} />}
                                    variant="ghost"
                                    size="xs"
                                    minW="auto"
                                    h="18px"
                                    px={1}
                                    color={sortOrder ? '#0071E3' : 'gray.400'}
                                    _hover={{ color: '#0071E3', bg: 'transparent' }}
                                    _active={{ bg: 'transparent' }}
                                    onClick={() => setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))}
                                />
                            </Flex>
                        </Th>
                        {comparison && (
                            <Th
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="semibold"
                                textTransform="capitalize"
                                letterSpacing="normal"
                                textAlign="right"
                                px={4}
                                py={2.5}
                                w="26%"
                            >
                                {comparisonLabel}
                            </Th>
                        )}
                        {showBreakdown && <Th w="36px" px={2} py={2.5} />}
                    </Tr>
                </Thead>
                <Tbody>
                    {rows.map((item) => {
                        const currentValue = typeof item[metric as keyof RankingItem] === 'number'
                            ? Number(item[metric as keyof RankingItem])
                            : 0;
                        const change = comparison ? getComparisonDisplay(item, metric, metricOption.format) : null;
                        const rowKey = item.id || item.name;
                        const sources = showBreakdown ? getRowSources(item) : [];
                        const isExpandable = sources.length > 0;
                        const isExpanded = isExpandable && expanded.has(rowKey);
                        return (
                            <Fragment key={rowKey}>
                            <Tr
                                cursor={onItemClick ? 'pointer' : undefined}
                                _hover={{ bg: 'gray.50' }}
                                onClick={() => onItemClick?.(item)}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                            >
                                <Td border="none" px={4} py={3}>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1} title={item.displayName}>
                                        {item.displayName}
                                    </Text>
                                </Td>
                                <Td border="none" px={4} py={3} textAlign="right">
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.900" noOfLines={1}>
                                        {formatter(currentValue)}
                                    </Text>
                                </Td>
                                {comparison && (
                                    <Td border="none" px={4} py={3} textAlign="right">
                                        {change ? (
                                            <Flex justify="flex-end">
                                                <Text
                                                    fontSize="xs"
                                                    fontWeight="semibold"
                                                    color={(NEGATIVE_METRICS.includes(metric as any) ? !change.isPositive : change.isPositive) ? 'green.500' : 'red.500'}
                                                    noOfLines={1}
                                                >
                                                    {change.text}
                                                </Text>
                                            </Flex>
                                        ) : (
                                            <Text fontSize="xs" color="gray.400">
                                                —
                                            </Text>
                                        )}
                                    </Td>
                                )}
                                {showBreakdown && (
                                    <Td border="none" px={1} py={3} textAlign="center">
                                        {isExpandable && (
                                            <IconButton
                                                aria-label={isExpanded ? 'Collapse marketplaces' : 'Expand marketplaces'}
                                                icon={<Icon as={(isExpanded ? TbChevronDown : TbChevronRight) as React.ElementType} boxSize={4} />}
                                                variant="ghost"
                                                size="xs"
                                                minW="auto"
                                                h="20px"
                                                color="gray.400"
                                                _hover={{ color: '#0071E3', bg: 'transparent' }}
                                                onClick={(e) => { e.stopPropagation(); toggleRow(rowKey); }}
                                            />
                                        )}
                                    </Td>
                                )}
                            </Tr>
                            {isExpanded && sources.map((src, i) => {
                                const sourceItem = item.bySource![src] as RankingItem;
                                const srcValue = typeof (sourceItem as any)[metric] === 'number' ? Number((sourceItem as any)[metric]) : 0;
                                const srcChange = comparison ? getComparisonDisplay(sourceItem, metric, metricOption.format) : null;
                                return (
                                    <Tr key={`${rowKey}-${src}`} bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
                                        <Td border="none" pl={10} pr={4} py={2}>
                                            <Flex align="center" gap={2}>
                                                <Box w="8px" h="8px" borderRadius="full" bg={getSourceColor(src, i)} flexShrink={0} />
                                                <Text fontSize="xs" color="gray.600" noOfLines={1}>{getSourceLabel(src)}</Text>
                                            </Flex>
                                        </Td>
                                        <Td border="none" px={4} py={2} textAlign="right">
                                            <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1}>
                                                {formatter(srcValue)}
                                            </Text>
                                        </Td>
                                        {comparison && (
                                            <Td border="none" px={4} py={2} textAlign="right">
                                                {srcChange ? (
                                                    <Text
                                                        fontSize="xs"
                                                        fontWeight="medium"
                                                        color={(NEGATIVE_METRICS.includes(metric as any) ? !srcChange.isPositive : srcChange.isPositive) ? 'green.500' : 'red.500'}
                                                        noOfLines={1}
                                                    >
                                                        {srcChange.text}
                                                    </Text>
                                                ) : (
                                                    <Text fontSize="xs" color="gray.400">—</Text>
                                                )}
                                            </Td>
                                        )}
                                        {showBreakdown && <Td border="none" px={1} py={2} />}
                                    </Tr>
                                );
                            })}
                            </Fragment>
                        );
                    })}
                </Tbody>
            </Table>
        </Box>
    );
};

export default RankingTableWidget;
